import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import {
  useGetHomeQuery,
  useUpdateActiveOrganisationMutation,
  useUploadFilesMutation,
} from "@/store/rest_api";
import { apiErrorMessage } from "@/util/apiError";
import { useAddOn } from "@/hooks/useAddOn";
import { AddOnType } from "@/enum/AddOnType";
import { RecordingPolicy, RECORDING_POLICY_OPTIONS } from "@/enum/RecordingPolicy";
import {
  DEFAULT_RECORDING_NOTICE,
  RecordingDisclosure,
  RECORDING_DISCLOSURE_OPTIONS,
} from "@/enum/RecordingDisclosure";
import { BUSINESS_TYPE_OPTIONS } from "@/enum/BusinessType";
import { useCanExport } from "@/hooks/useCanExport";
import { organisationLogoUrl } from "@/util/orgLogo";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Anchorage",
  "Pacific/Honolulu",
];

const COUNTRIES = [
  { code: "US", name: "United States" },
  { code: "CA", name: "Canada" },
  { code: "GB", name: "United Kingdom" },
  { code: "AU", name: "Australia" },
];

interface OrgFormState {
  name: string;
  description: string;
  website: string;
  phone: string;
  country: string;
  timezone: string;
  businessType: string;
  mailingAddressLine1: string;
  mailingAddressLine2: string;
  mailingCity: string;
  mailingState: string;
  mailingPostalCode: string;
  mailingCountry: string;
}

const EMPTY_FORM: OrgFormState = {
  name: "",
  description: "",
  website: "",
  phone: "",
  country: "US",
  timezone: "America/New_York",
  businessType: "",
  mailingAddressLine1: "",
  mailingAddressLine2: "",
  mailingCity: "",
  mailingState: "",
  mailingPostalCode: "",
  mailingCountry: "US",
};

export default function OrganisationSettings() {
  const navigate = useNavigate();
  const { data: user, isLoading } = useGetHomeQuery();
  const organisation = user?.activeOrganisation;
  const [updateOrganisation, { isLoading: isSaving }] = useUpdateActiveOrganisationMutation();
  const [uploadFiles, { isLoading: isUploading }] = useUploadFilesMutation();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState<OrgFormState>(EMPTY_FORM);
  const [logoUrl, setLogoUrl] = useState<string | undefined>(undefined);
  const [logoFileId, setLogoFileId] = useState<string | undefined>(undefined);
  const [autoOutreach, setAutoOutreach] = useState(false);
  const [recordingPolicy, setRecordingPolicy] = useState<RecordingPolicy>(RecordingPolicy.ALWAYS);
  const [recordingDisclosure, setRecordingDisclosure] = useState<RecordingDisclosure>(RecordingDisclosure.ALWAYS);
  const [confirmDisclosureOff, setConfirmDisclosureOff] = useState(false);
  const [announcementDialogOpen, setAnnouncementDialogOpen] = useState(false);
  const [announcementChoice, setAnnouncementChoice] = useState<RecordingDisclosure>(RecordingDisclosure.WHERE_REQUIRED);
  const [recordingNotice, setRecordingNotice] = useState("");
  const hasCallRecording = useAddOn(AddOnType.CALL_RECORDING);
  const { canExport } = useCanExport();

  const set = (field: keyof OrgFormState, value: string) =>
    setForm((current) => ({ ...current, [field]: value }));

  const onUploadLogo = async (file: File) => {
    try {
      const result = await uploadFiles({ files: [file], type: "LOGO" }).unwrap();
      const uploaded = result?.[0];
      if (uploaded?.id) {
        setLogoFileId(uploaded.id);
        setLogoUrl(uploaded.url);
        toast.success("Logo uploaded. Save to apply.");
      }
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to upload logo"));
    }
  };

  const onSave = async () => {
    if (!form.name.trim()) {
      toast.error("Organisation name is required");
      return;
    }
    try {
      await updateOrganisation({
        name: form.name.trim(),
        description: form.description.trim(),
        website: form.website.trim(),
        phone: form.phone.trim(),
        country: form.country,
        timezone: form.timezone,
        businessType: form.businessType || undefined,
        mailingAddressLine1: form.mailingAddressLine1.trim(),
        mailingAddressLine2: form.mailingAddressLine2.trim(),
        mailingCity: form.mailingCity.trim(),
        mailingState: form.mailingState.trim(),
        mailingPostalCode: form.mailingPostalCode.trim(),
        mailingCountry: form.mailingCountry.trim(),
        ...(logoFileId ? { logoFileId } : {}),
      }).unwrap();
      toast.success("Organisation updated");
      setLogoFileId(undefined);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to update organisation"));
    }
  };

  const onToggleAutoOutreach = async (checked: boolean) => {
    setAutoOutreach(checked);
    try {
      await updateOrganisation({ remindersAutoOutreachEnabled: checked }).unwrap();
      toast.success(checked ? "Auto-outreach enabled" : "Auto-outreach disabled");
    } catch (error) {
      setAutoOutreach(!checked);
      toast.error(apiErrorMessage(error, "Failed to update"));
    }
  };

  const onRecordingPolicyChange = async (value: string) => {
    const previous = recordingPolicy;
    const policy = value as RecordingPolicy;
    setRecordingPolicy(policy);
    try {
      await updateOrganisation({ recordingPolicy: policy }).unwrap();
      toast.success("Recording policy updated");
      if (previous === RecordingPolicy.NEVER && policy !== RecordingPolicy.NEVER) {
        setAnnouncementChoice(recordingDisclosure);
        setAnnouncementDialogOpen(true);
      }
    } catch (error) {
      setRecordingPolicy(previous);
      toast.error(apiErrorMessage(error, "Failed to update"));
    }
  };

  const saveRecordingNotice = async () => {
    const notice = recordingNotice.trim();
    if (notice === (organisation?.recordingNotice ?? "")) {
      return;
    }
    try {
      await updateOrganisation({ recordingNotice: notice }).unwrap();
      toast.success("Recording announcement updated");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to update"));
    }
  };

  const saveRecordingDisclosure = async (disclosure: RecordingDisclosure) => {
    const previous = recordingDisclosure;
    setRecordingDisclosure(disclosure);
    try {
      await updateOrganisation({ recordingDisclosure: disclosure }).unwrap();
      toast.success(
        disclosure === RecordingDisclosure.NEVER
          ? "Recording announcement turned off"
          : "Recording announcement updated",
      );
    } catch (error) {
      setRecordingDisclosure(previous);
      toast.error(apiErrorMessage(error, "Failed to update"));
    }
  };

  const onRecordingDisclosureChange = (value: string) => {
    const disclosure = value as RecordingDisclosure;
    if (disclosure === RecordingDisclosure.NEVER) {
      setConfirmDisclosureOff(true);
      return;
    }
    void saveRecordingDisclosure(disclosure);
  };

  const saveAnnouncementChoice = () => {
    setAnnouncementDialogOpen(false);
    if (announcementChoice !== recordingDisclosure) {
      void saveRecordingDisclosure(announcementChoice);
    }
  };

  useEffect(() => {
    if (organisation) {
      setForm({
        name: organisation.name ?? "",
        description: organisation.description ?? "",
        website: organisation.website ?? "",
        phone: organisation.phone ?? "",
        country: organisation.country ?? "US",
        timezone: organisation.timezone ?? "America/New_York",
        businessType: organisation.businessType ?? "",
        mailingAddressLine1: organisation.mailingAddressLine1 ?? "",
        mailingAddressLine2: organisation.mailingAddressLine2 ?? "",
        mailingCity: organisation.mailingCity ?? "",
        mailingState: organisation.mailingState ?? "",
        mailingPostalCode: organisation.mailingPostalCode ?? "",
        mailingCountry: organisation.mailingCountry ?? "US",
      });
      setLogoUrl(organisationLogoUrl(organisation.logoFileId));
      setAutoOutreach(organisation.remindersAutoOutreachEnabled ?? false);
      setRecordingPolicy(organisation.recordingPolicy ?? RecordingPolicy.ALWAYS);
      setRecordingDisclosure(organisation.recordingDisclosure ?? RecordingDisclosure.ALWAYS);
      setRecordingNotice(organisation.recordingNotice ?? "");
    }
  }, [organisation]);

  if (isLoading) {
    return (
      <div className="p-4 text-foreground">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="mt-6 h-96 w-full max-w-2xl" />
      </div>
    );
  }

  return (
    <div className="p-4 text-foreground">
      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight">Organisation settings</h1>
        <p className="text-muted-foreground text-sm">Your business profile, logo, and localization.</p>
      </div>

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle>Business profile</CardTitle>
          <CardDescription>Details shown on your account, emails, and tenant sites.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label>Logo</Label>
            <div className="flex items-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-md border bg-muted">
                {logoUrl ? (
                  <img src={logoUrl} alt="Organisation logo" className="h-full w-full object-contain" />
                ) : (
                  <span className="text-center text-xs text-muted-foreground">No logo</span>
                )}
              </div>
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(changeEvent) => {
                    const file = changeEvent.target.files?.[0];
                    if (file) {
                      onUploadLogo(file);
                    }
                    changeEvent.target.value = "";
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={isUploading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {isUploading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="mr-2 h-4 w-4" />
                  )}
                  Upload
                </Button>
                <p className="mt-1 text-xs text-muted-foreground">PNG or JPG. Square works best.</p>
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="org-name">Organisation name</Label>
            <Input id="org-name" value={form.name} onChange={(changeEvent) => set("name", changeEvent.target.value)} />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Timezone</Label>
              <Select value={form.timezone} onValueChange={(value) => set("timezone", value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select timezone" />
                </SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map((zone) => (
                    <SelectItem key={zone} value={zone}>
                      {zone.replace(/_/g, " ")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Business type</Label>
              <Select value={form.businessType} onValueChange={(value) => set("businessType", value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {BUSINESS_TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Country</Label>
              <Select value={form.country} onValueChange={(value) => set("country", value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map((country) => (
                    <SelectItem key={country.code} value={country.code}>
                      {country.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="org-phone">Phone</Label>
              <Input id="org-phone" value={form.phone} onChange={(changeEvent) => set("phone", changeEvent.target.value)} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="org-website">Website</Label>
            <Input
              id="org-website"
              value={form.website}
              placeholder="https://"
              onChange={(changeEvent) => set("website", changeEvent.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="org-description">Description</Label>
            <Textarea
              id="org-description"
              value={form.description}
              onChange={(changeEvent) => set("description", changeEvent.target.value)}
            />
          </div>

          <div className="space-y-3 rounded-lg border p-4">
            <div>
              <Label>Mailing address</Label>
              <p className="text-xs text-muted-foreground">
                Marketing email has to carry a postal address by law, and it appears in the footer of every email your
                workflows send. Without one, workflow emails are not sent.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="org-mailing-line1">Street address</Label>
              <Input
                id="org-mailing-line1"
                value={form.mailingAddressLine1}
                onChange={(changeEvent) => set("mailingAddressLine1", changeEvent.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="org-mailing-line2">Suite, unit, floor (optional)</Label>
              <Input
                id="org-mailing-line2"
                value={form.mailingAddressLine2}
                onChange={(changeEvent) => set("mailingAddressLine2", changeEvent.target.value)}
              />
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label htmlFor="org-mailing-city">City</Label>
                <Input
                  id="org-mailing-city"
                  value={form.mailingCity}
                  onChange={(changeEvent) => set("mailingCity", changeEvent.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="org-mailing-state">State</Label>
                <Input
                  id="org-mailing-state"
                  value={form.mailingState}
                  onChange={(changeEvent) => set("mailingState", changeEvent.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="org-mailing-postal">Postal code</Label>
                <Input
                  id="org-mailing-postal"
                  value={form.mailingPostalCode}
                  onChange={(changeEvent) => set("mailingPostalCode", changeEvent.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={onSave} disabled={isSaving}>
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save changes
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="mt-4 max-w-2xl">
        <CardHeader>
          <CardTitle>Reminders</CardTitle>
          <CardDescription>How reminder workflows are allowed to reach out.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-start justify-between gap-4">
            <div>
              <Label>Allow auto-outreach to leads</Label>
              <p className="mt-1 text-xs text-muted-foreground">
                When on, reminder workflows can automatically contact the lead (auto-text, auto-email). When off,
                reminders only notify your team. Auto-outreach is consent-gated.
              </p>
            </div>
            <Switch checked={autoOutreach} onCheckedChange={onToggleAutoOutreach} />
          </div>
        </CardContent>
      </Card>

      <Card className="mt-4 max-w-2xl">
        <CardHeader>
          <CardTitle>Call recording</CardTitle>
          <CardDescription>When calls are recorded, and what inbound callers are told.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!hasCallRecording && (
            <div className="rounded-md border border-dashed p-3">
              <p className="text-sm font-medium">Call recording is not enabled</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Recording is a paid add-on. Until it is enabled no calls are recorded, on the dialer or the power
                dialer, and this policy has no effect.
              </p>
              <Button variant="outline" size="sm" className="mt-3" onClick={() => navigate("/manage-organisation/billing/add-ons")}>
                Enable the add-on
              </Button>
            </div>
          )}
          <div className="flex items-start justify-between gap-4">
            <div>
              <Label>Recording policy</Label>
              <p className="mt-1 text-xs text-muted-foreground">
                Always record starts every call recording automatically. Agent controlled lets agents start, pause
                and stop from the in-call REC control. Never record turns recording off everywhere.
              </p>
            </div>
            <Select value={recordingPolicy} onValueChange={onRecordingPolicyChange} disabled={!hasCallRecording}>
              <SelectTrigger className="w-52 shrink-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RECORDING_POLICY_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-start justify-between gap-4">
            <div>
              <Label>Recording announcement</Label>
              <p className="mt-1 text-xs text-muted-foreground">
                The other person hears a short notice before the call connects, on inbound, outbound, power dialer and
                AI calls. It only plays while the policy is set to always record. Some states need every person on the
                call to be told, so where that applies and the announcement is off, the call is not recorded at all.
              </p>
            </div>
            <Select
              value={recordingDisclosure}
              onValueChange={onRecordingDisclosureChange}
              disabled={!hasCallRecording}
            >
              <SelectTrigger className="w-52 shrink-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RECORDING_DISCLOSURE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {recordingDisclosure !== RecordingDisclosure.NEVER && (
            <div className="space-y-1.5">
              <Label htmlFor="recording-notice">What the announcement says</Label>
              <Input
                id="recording-notice"
                value={recordingNotice}
                maxLength={500}
                disabled={!hasCallRecording}
                placeholder={DEFAULT_RECORDING_NOTICE}
                onChange={(event) => setRecordingNotice(event.target.value)}
                onBlur={saveRecordingNotice}
              />
              <p className="text-xs text-muted-foreground">
                Leave it empty to use "{DEFAULT_RECORDING_NOTICE}". Keep it short and say plainly that the call is
                recorded, since that is what the stricter states ask for.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {canExport && (
        <Card className="mt-4 max-w-2xl">
          <CardHeader>
            <CardTitle>Export</CardTitle>
            <CardDescription>Download your workspace data and see every export made from this workspace.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" onClick={() => navigate("/manage-organisation/settings/export")}>
              Open export
            </Button>
          </CardContent>
        </Card>
      )}

      <AlertDialog open={confirmDisclosureOff} onOpenChange={setConfirmDisclosureOff}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Turn off the recording announcement?</AlertDialogTitle>
            <AlertDialogDescription>
              Some states require every person on a call to be told before recording starts. With the announcement off,
              calls to those states are not recorded at all, because recording them without notice would break the law.
              Calls everywhere else keep recording.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmDisclosureOff(false);
                void saveRecordingDisclosure(RecordingDisclosure.NEVER);
              }}
            >
              Turn it off
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={announcementDialogOpen} onOpenChange={setAnnouncementDialogOpen}>
        <DialogContent className="max-w-md text-foreground">
          <DialogHeader>
            <DialogTitle>Choose a recording announcement</DialogTitle>
            <DialogDescription>
              We highly recommend an announcement, unless your team will say the call is recorded at the start of every
              call.
            </DialogDescription>
          </DialogHeader>
          <RadioGroup
            value={announcementChoice}
            onValueChange={(value) => setAnnouncementChoice(value as RecordingDisclosure)}
            className="gap-3"
          >
            {RECORDING_DISCLOSURE_OPTIONS.map((option) => (
              <div key={option.value} className="flex items-start gap-2">
                <RadioGroupItem value={option.value} id={`announcement-${option.value}`} className="mt-0.5" />
                <Label htmlFor={`announcement-${option.value}`} className="space-y-0.5 font-normal">
                  <span className="block text-sm font-medium">{option.label}</span>
                  <span className="block text-xs text-muted-foreground">{option.description}</span>
                </Label>
              </div>
            ))}
          </RadioGroup>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAnnouncementDialogOpen(false)}>
              Not now
            </Button>
            <Button onClick={saveAnnouncementChoice}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
